import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { createPortal } from 'react-dom';

export interface DropdownOption {
  value: string;
  label: string;
  shortLabel?: string;
  color?: string;
}

interface DropdownSelectProps {
  value: string;
  options: DropdownOption[];
  onChange: (value: string) => void;
  ariaLabel: string;
  leading?: ReactNode;
  className?: string;
  disabled?: boolean;
  variant?: 'pill' | 'input';
}

export function DropdownSelect({
  value,
  options,
  onChange,
  ariaLabel,
  leading,
  className = '',
  disabled = false,
  variant = 'pill',
}: DropdownSelectProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuStyle, setMenuStyle] = useState({ left: 0, top: 0, width: 0 });
  const [opensAbove, setOpensAbove] = useState(false);
  const selected = options.find((option) => option.value === value) || options[0];

  const positionMenu = useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) {
      const estimatedHeight = Math.min(288, options.length * 32 + 8);
      const above =
        rect.bottom + estimatedHeight > window.innerHeight && rect.top > estimatedHeight;
      setOpensAbove(above);
      setMenuStyle({
        left: rect.left,
        top: above ? rect.top - estimatedHeight + 1 : rect.bottom - 1,
        width: rect.width,
      });
    }
  }, [options.length]);

  useEffect(() => {
    const close = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!rootRef.current?.contains(target) && !menuRef.current?.contains(target)) setOpen(false);
    };
    window.addEventListener('mousedown', close);
    return () => window.removeEventListener('mousedown', close);
  }, []);

  useEffect(() => {
    if (!open) return;
    positionMenu();
    window.addEventListener('resize', positionMenu);
    window.addEventListener('scroll', positionMenu, true);
    return () => {
      window.removeEventListener('resize', positionMenu);
      window.removeEventListener('scroll', positionMenu, true);
    };
  }, [open, positionMenu]);

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={() => {
          positionMenu();
          setOpen((current) => !current);
        }}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`flex h-9 w-full items-center gap-2 border bg-atlas-surface px-3 text-left text-xs text-atlas-ink outline-none transition disabled:cursor-not-allowed disabled:bg-atlas-subtle disabled:opacity-50 ${open ? 'rounded-t-lg rounded-b-none border-atlas-border-strong shadow-sm' : `${variant === 'input' ? 'rounded-lg' : 'rounded-full'} border-atlas-border hover:border-atlas-border-strong`}`}
      >
        {leading ||
          (selected?.color && (
            <span
              className="h-3.5 w-3.5 shrink-0 rounded-full"
              style={{ backgroundColor: selected.color }}
            />
          ))}
        <span className="min-w-0 flex-1 truncate">
          {selected?.shortLabel || selected?.label || 'Select'}
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-atlas-muted transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            data-atlas-dropdown-portal
            role="listbox"
            aria-label={ariaLabel}
            style={menuStyle}
            className={`fixed z-[200] max-h-72 overflow-y-auto border border-atlas-border-strong bg-atlas-elevated py-1 shadow-atlas-float ${opensAbove ? 'rounded-t-lg border-b-0' : 'rounded-b-lg border-t-0'}`}
          >
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={option.value === value}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                className={`flex min-h-8 w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition hover:bg-atlas-subtle ${option.value === value ? 'font-medium text-atlas-ink' : 'text-atlas-muted'}`}
              >
                {option.color && (
                  <span
                    className="h-3.5 w-3.5 shrink-0 rounded-full"
                    style={{ backgroundColor: option.color }}
                  />
                )}
                <span className="min-w-0 flex-1 truncate">{option.label}</span>
                {option.value === value && <Check className="h-4 w-4 shrink-0 text-atlas-blue" />}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}
